import React, { useEffect } from 'react';
import { usePageSeo } from '../hooks/usePageSeo';
import { Button } from '../components/ui/button';
import { ChevronRight } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import Header from '../components/Header';

const About = () => {
  const navigate = useNavigate();

  usePageSeo({
    title: 'About Us | Civic Rentals LLC',
    description: 'Learn about Civic Rentals LLC\u2019 commitment to customer service and our mission to help local governments manage short-term rental programs.',
    canonical: 'https://civicrentals.com/about',
    ogTitle: 'About Us | Civic Rentals LLC',
    ogDescription: 'Learn about Civic Rentals LLC\u2019 commitment to customer service and our mission to help local governments manage short-term rental programs.',
    ogUrl: 'https://civicrentals.com/about',
    ogImage: 'https://civicrentals.com/favicon.png',
    twitterCard: 'summary',
  });

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#d5e0de' }}>

      <Header active="About Us" />

      <section className="pt-32 pb-16 px-6">
        <div className="container mx-auto max-w-3xl">
          {/* Breadcrumbs */}
          <nav aria-label="Breadcrumb" className="mb-6" data-testid="breadcrumbs">
            <ol className="flex items-center gap-1.5 text-sm text-slate-500">
              <li><Link to="/" className="hover:text-slate-800 transition-colors">Home</Link></li>
              <li><ChevronRight className="h-3.5 w-3.5" /></li>
              <li className="text-slate-700 font-medium">About Us</li>
            </ol>
          </nav>

          {/* Our Commitment to Customer Service */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden mb-8">
            <div className="px-8 py-8 sm:px-10 sm:py-10">
              <h1 className="text-3xl font-bold text-slate-900 mb-6">Our Commitment to Customer Service</h1>
              <p className="text-slate-700 leading-relaxed mb-4">
                At Civic Rentals LLC, we believe great software is only part of a successful short-term rental (STR) program. Responsive, knowledgeable support is just as important. Whether you're implementing a new short-term rental registration program, updating an ordinance, or troubleshooting a day-to-day question, you'll have direct access to our team—not an endless ticket queue.
              </p>
              <p className="text-slate-700 leading-relaxed mb-4">
                When you need help, you'll be able to reach a real person by phone, email, or screen share. We work alongside local governments to answer questions, solve problems, and keep your short-term rental (STR) program moving forward. Every support request is acknowledged within one business day, and we strive to resolve issues as quickly as possible so your staff can stay focused on serving your community.
              </p>
              <p className="text-slate-700 leading-relaxed">
                Our goal is simple: build long-term partnerships by delivering dependable software backed by responsive, personal service.
              </p>
            </div>
          </div>

          {/* Helping Your Team */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden mb-8">
            <div className="px-8 py-8 sm:px-10 sm:py-10">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">Helping Your Team Focus on What Matters</h2>
              <p className="text-slate-700 leading-relaxed mb-4">
                Local governments have limited staff and growing responsibilities. Our goal is to reduce the time your team spends identifying short-term rentals (STRs), managing registrations, processing permits, responding to operators, and tracking program activity, so they can spend more time serving constituents and administering successful programs.
              </p>
              <p className="text-slate-700 leading-relaxed">
                We believe government software should simplify work—not create more of it. That's why we pair intuitive technology with boutique, white-glove support at an affordable price. Whether you need help onboarding, answering a question, or working through a complex issue, you'll have a knowledgeable partner who understands short-term rental (STR) programs and is ready to help.
              </p>
            </div>
          </div>

          {/* TEMPORARILY HIDDEN — About the Founder — uncomment to restore */}
        </div>
      </section>

      <footer className="bg-slate-900 text-slate-400 py-12 px-6 border-t border-slate-800">
        <div className="container mx-auto max-w-6xl">
          <div className="border-t border-slate-800 pt-8 text-center text-sm">
            <p>&copy; 2026 Civic Rentals LLC. All rights reserved.</p>
            <p className="mt-2 space-x-4">
              <a href="/privacypolicy" className="hover:text-white transition-colors">Privacy Policy</a>
              <a href="/websiteTOS" className="hover:text-white transition-colors">Terms of Service</a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default About;
